<template>
  <div class="workoutBasicForm workoutCollectionForm">
    <div class="workoutMetaStrip workoutMetaStrip--collection">
      <el-form-item :label="L.displayName" required>
        <el-input v-model="local.display_name" />
      </el-form-item>
      <el-form-item :label="L.subtitle">
        <el-input v-model="local.subtitle" />
      </el-form-item>
      <el-form-item :label="L.difficulty" required>
        <el-select v-model="local.difficulty">
          <el-option
            v-for="opt in difficultyOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
      </el-form-item>
    </div>
    <el-form-item :label="L.description">
      <el-input
        v-model="local.description"
        type="textarea"
        :rows="3"
        :placeholder="descriptionPlaceholder"
      />
    </el-form-item>

    <workout-collection-binding-panel
      ref="bindingPanel"
      :actions.sync="local.actions"
      :music.sync="local.music"
      :voices.sync="local.voices"
    />
  </div>
</template>

<script>
import basicFormMixin from './basicFormMixin'
import WorkoutCollectionBindingPanel from './WorkoutCollectionBindingPanel.vue'
import { WORKOUT_FIELD_LABELS, WORKOUT_DIFFICULTY_OPTIONS, WORKOUT_DESCRIPTION_PLACEHOLDERS, normalizeWorkoutDifficulty } from './workoutFormConstants'
import {
  computeTotalDurationSeconds,
  sumActionDurationSeconds,
} from './workoutActionBindingCalculator'
import {
  sanitizeActionBindingsForSave,
  sanitizeCollectionBindingRow,
  validateWorkoutCollectionBindings,
} from './workoutCollectionValidation'
import {
  emptyCollectionForm,
  loadCollectionBindingsFromExta,
} from './workoutCollectionBindingModel'
import './workoutFormLayout.css'

/**
 * workout_collection 基础表单壳：持有 exta 编辑态与 basicFormMixin 契约；
 * 三区 binding 编排委托 WorkoutCollectionBindingPanel。
 */
export default {
  name: 'WorkoutCollectionBasicForm',
  components: { WorkoutCollectionBindingPanel },
  mixins: [basicFormMixin],
  props: {
    ruleForm: { type: Object, default: null },
  },
  data() {
    return {
      local: emptyCollectionForm(),
      L: WORKOUT_FIELD_LABELS,
    }
  },
  computed: {
    difficultyOptions() {
      return WORKOUT_DIFFICULTY_OPTIONS
    },
    descriptionPlaceholder() {
      return WORKOUT_DESCRIPTION_PLACEHOLDERS.workout_collection
    },
  },
  watch: {
    local: {
      deep: true,
      handler() {
        this.scheduleCompletionEmit()
      },
    },
  },
  methods: {
    buildExtaPayload() {
      // sanitize 时已按双视角写入每条 duration_seconds；总时长必须 sum 这些值，
      // 不能再对瘦身行 computeTotalDurationSeconds（会丢侧视角、把总时长算矮）。
      const actions = sanitizeActionBindingsForSave(this.local.actions)
      const music = this.local.music.map((row) => sanitizeCollectionBindingRow('music', row))
      const voices = this.local.voices.map((row) => sanitizeCollectionBindingRow('voices', row))
      return {
        display_name: this.local.display_name,
        subtitle: this.local.subtitle,
        difficulty: this.local.difficulty,
        description: this.local.description,
        total_duration_seconds: sumActionDurationSeconds(actions),
        actions,
        music,
        voices,
      }
    },
    loadFromExta(exta) {
      const data = exta || {}
      this.local = {
        ...emptyCollectionForm(),
        display_name: data.display_name || '',
        subtitle: data.subtitle || '',
        difficulty: normalizeWorkoutDifficulty(data.difficulty),
        description: data.description || '',
        ...loadCollectionBindingsFromExta(data),
      }
      this.$nextTick(async () => {
        const panel = this.$refs.bindingPanel
        if (panel) {
          await panel.refreshAllBindingsFromServer({ notifyOnChange: true })
          panel.reinitZoneSortables()
        }
      })
    },
    reset() {
      this.local = emptyCollectionForm()
    },
    async validate() {
      if (!String(this.local.display_name || '').trim()) {
        return Promise.reject(`请填写 ${WORKOUT_FIELD_LABELS.displayName}`)
      }
      if (!this.local.difficulty) {
        return Promise.reject(`请选择 ${WORKOUT_FIELD_LABELS.difficulty}`)
      }
      // 保存前先拉齐子资源 ref_meta：避免双视角合集在 refresh 完成前按主视角写错时长
      const panel = this.$refs.bindingPanel
      if (panel && typeof panel.refreshAllBindingsFromServer === 'function') {
        await panel.refreshAllBindingsFromServer({ silent: true, notifyOnChange: false })
      }
      // 与落库总时长对齐：编辑态重算 → 应等于 sanitize 后 sumActionDurationSeconds
      // 缺说明书由 validateWorkoutCollectionBindings 统一拒绝
      return validateWorkoutCollectionBindings({
        ...this.local,
        total_duration_seconds: computeTotalDurationSeconds(this.local.actions),
      })
    },
    captureSnapshot() {
      return JSON.parse(JSON.stringify(this.local))
    },
  },
}
</script>
